import { createTheme } from "@mui/material/styles";

// Function to get theme based on mode (light/dark)
export const getTheme = (mode = 'light') => {
  const isLight = mode === 'light';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: isLight ? "#123ADD" : "#4A90E2", // Brand Blue (lighter for dark mode)
        light: isLight ? "#4A5FE6" : "#64A8F5",
        dark: isLight ? "#0D2AB3" : "#2563EB",
        contrastText: "#ffffff",
      },
      secondary: {
        main: isLight ? "#ec4899" : "#f472b6", // Pink (lighter for dark mode)
        light: isLight ? "#f472b6" : "#f9a8d4",
        dark: isLight ? "#db2777" : "#ec4899",
        contrastText: "#ffffff",
      },
      success: {
        main: isLight ? "#10b981" : "#34d399", // Green
        light: isLight ? "#34d399" : "#6ee7b7",
        dark: isLight ? "#059669" : "#10b981",
      },
      warning: {
        main: isLight ? "#f59e0b" : "#fbbf24", // Amber
        light: isLight ? "#fbbf24" : "#fcd34d",
        dark: isLight ? "#d97706" : "#f59e0b",
      },
      error: {
        main: isLight ? "#ef4444" : "#f87171", // Red
        light: isLight ? "#f87171" : "#fca5a5",
        dark: isLight ? "#dc2626" : "#ef4444",
      },
      info: {
        main: isLight ? "#3b82f6" : "#60a5fa", // Blue
        light: isLight ? "#60a5fa" : "#93c5fd",
        dark: isLight ? "#2563eb" : "#3b82f6",
      },
      background: {
        default: isLight ? "#f8fafc" : "#0f172a", // Light gray / Dark slate
        paper: isLight ? "#ffffff" : "#1e293b",
      },
      text: {
        primary: isLight ? "#1e293b" : "#f1f5f9", // Slate / Light slate
        secondary: isLight ? "#64748b" : "#cbd5e1",
      },
      divider: isLight ? "#e2e8f0" : "#334155",
    },
    typography: {
      fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
      h1: {
        fontSize: "2.5rem",
        fontWeight: 700,
        letterSpacing: "-0.02em",
      },
      h2: {
        fontSize: "2rem",
        fontWeight: 700,
        letterSpacing: "-0.01em",
      },
      h3: {
        fontSize: "1.75rem",
        fontWeight: 600,
        letterSpacing: "-0.01em",
      },
      h4: {
        fontSize: "1.5rem",
        fontWeight: 600,
      },
      h5: {
        fontSize: "1.25rem",
        fontWeight: 600,
      },
      h6: {
        fontSize: "1.125rem",
        fontWeight: 600,
      },
      subtitle1: {
        fontSize: "1rem",
        fontWeight: 500,
        lineHeight: 1.75,
      },
      subtitle2: {
        fontSize: "0.875rem",
        fontWeight: 500,
        lineHeight: 1.57,
      },
      body1: {
        fontSize: "1rem",
        lineHeight: 1.75,
      },
      body2: {
        fontSize: "0.875rem",
        lineHeight: 1.57,
      },
      button: {
        fontWeight: 600,
        textTransform: "none",
        letterSpacing: "0.02em",
      },
    },
    shape: {
      borderRadius: 12,
    },
    shadows: [
      "none",
      isLight
        ? "0 1px 2px 0 rgba(0, 0, 0, 0.05)"
        : "0 1px 2px 0 rgba(0, 0, 0, 0.3)",
      isLight
        ? "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)"
        : "0 1px 3px 0 rgba(0, 0, 0, 0.4), 0 1px 2px 0 rgba(0, 0, 0, 0.24)",
      isLight
        ? "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
        : "0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.24)",
      isLight
        ? "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)"
        : "0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.2)",
      isLight
        ? "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
        : "0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.16)",
      "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    ],
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: "10px 20px",
            boxShadow: "none",
            "&:hover": {
              boxShadow: isLight
                ? "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
                : "0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.24)",
            },
          },
          contained: {
            "&:hover": {
              boxShadow: isLight
                ? "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)"
                : "0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.2)",
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
          elevation1: {
            boxShadow: isLight
              ? "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)"
              : "0 1px 3px 0 rgba(0, 0, 0, 0.4), 0 1px 2px 0 rgba(0, 0, 0, 0.24)",
          },
          elevation2: {
            boxShadow: isLight
              ? "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
              : "0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.24)",
          },
          elevation3: {
            boxShadow: isLight
              ? "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)"
              : "0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.2)",
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: isLight
              ? "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)"
              : "0 1px 3px 0 rgba(0, 0, 0, 0.4), 0 1px 2px 0 rgba(0, 0, 0, 0.24)",
            transition: "all 0.3s ease-in-out",
            "&:hover": {
              boxShadow: isLight
                ? "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)"
                : "0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.2)",
              transform: "translateY(-4px)",
            },
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: isLight ? "#f8fafc" : "#0f172a",
            "& .MuiTableCell-head": {
              fontWeight: 600,
              color: isLight ? "#475569" : "#94a3b8",
              textTransform: "uppercase",
              fontSize: "0.75rem",
              letterSpacing: "0.05em",
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: isLight ? "1px solid #f1f5f9" : "1px solid #334155",
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: isLight
              ? "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)"
              : "0 1px 3px 0 rgba(0, 0, 0, 0.4), 0 1px 2px 0 rgba(0, 0, 0, 0.24)",
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: isLight ? "1px solid #e2e8f0" : "1px solid #334155",
            boxShadow: "none",
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            borderRadius: 6,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 8,
            },
          },
        },
      },
    },
  });
};

// Default light theme export for backward compatibility
const theme = getTheme('light');
export default theme;
